import { useEffect, useRef, useState } from 'react';
import { CRASH_GROWTH_K } from '@raketta/shared';
import { useAppStore } from '../store/useAppStore';
import { getAuthToken } from '../api/client';
import { getCrashSocket } from '../lib/crash';
import { sound } from '../lib/sound';
import { telegram } from '../lib/telegram';
import { formatTon } from '../lib/format';
import { replayLink } from '../lib/appConfig';

type Phase = 'connecting' | 'betting' | 'running' | 'crashed' | 'cooldown';

interface Player {
  userId: number;
  name: string;
  amount: string;
  autoCashout: number | null;
  cashedOut: boolean;
  cashMultiplier: number | null;
  payout: string | null;
}
interface MyBet {
  amount: string;
  autoCashout: number | null;
  cashedOut: boolean;
  cashMultiplier: number | null;
  payout: string | null;
}

interface Star { x: number; y: number; r: number; s: number }
interface Particle { x: number; y: number; vx: number; vy: number; life: number; c: string }

export function CrashScreen() {
  const balance = useAppStore((s) => s.balance);
  const setBalance = useAppStore((s) => s.setBalance);
  const showToast = useAppStore((s) => s.showToast);

  const [phase, setPhase] = useState<Phase>('connecting');
  const [recent, setRecent] = useState<number[]>([]);
  const [players, setPlayers] = useState<Player[]>([]);
  const [myBet, setMyBet] = useState<MyBet | null>(null);
  const [amount, setAmount] = useState('10');
  const [autoCash, setAutoCash] = useState('2.00');
  const [busy, setBusy] = useState(false);
  const [lastWin, setLastWin] = useState<{ betId: number; multiplier: number; payout: string } | null>(null);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const cashRef = useRef<HTMLButtonElement | null>(null);
  const myBetRef = useRef<MyBet | null>(null);
  myBetRef.current = myBet;

  // мутабельное состояние симуляции для rAF
  const sim = useRef({
    phase: 'connecting' as Phase,
    startedAt: 0,
    offset: 0,
    crashPoint: 0,
    betEndsAt: 0,
    mult: 1,
    shake: 0,
    stars: [] as Star[],
    particles: [] as Particle[],
    explodedAt: 0,
  });

  const serverNow = () => Date.now() + sim.current.offset;

  useEffect(() => {
    const s = getCrashSocket();

    const applyState = (st: any) => {
      sim.current.offset = (st.now ?? Date.now()) - Date.now();
      sim.current.phase = st.phase;
      sim.current.startedAt = st.startedAt ?? 0;
      sim.current.crashPoint = st.crashPoint ?? 0;
      sim.current.betEndsAt = st.betEndsAt ?? 0;
      setPhase(st.phase);
      if (st.recent) setRecent(st.recent);
      if (st.players) setPlayers(st.players);
      const me = st.players?.find((p: Player) => p.userId === currentUserId());
      if (me) setMyBet({ amount: me.amount, autoCashout: me.autoCashout, cashedOut: me.cashedOut, cashMultiplier: me.cashMultiplier, payout: me.payout });
    };

    const onState = (st: any) => applyState(st);
    const onBetting = (e: any) => {
      sim.current.offset = e.now - Date.now();
      sim.current.phase = 'betting';
      sim.current.betEndsAt = e.betEndsAt;
      sim.current.crashPoint = 0;
      sim.current.mult = 1;
      sim.current.particles = [];
      setPhase('betting');
      setMyBet(null);
      setLastWin(null);
      setPlayers([]);
      if (e.recent) setRecent(e.recent);
    };
    const onBet = (e: any) => {
      setPlayers((ps) => [
        ...ps.filter((p) => p.userId !== e.userId),
        { userId: e.userId, name: e.name, amount: e.amount, autoCashout: e.autoCashout, cashedOut: false, cashMultiplier: null, payout: null },
      ]);
    };
    const onStart = (e: any) => {
      sim.current.offset = e.now - Date.now();
      sim.current.phase = 'running';
      sim.current.startedAt = e.startedAt;
      sim.current.mult = 1;
      setPhase('running');
      sound.spin();
    };
    const onTick = (e: any) => {
      if (e.now) sim.current.offset = e.now - Date.now();
    };
    const onCashout = (e: any) => {
      setPlayers((ps) => ps.map((p) => (p.userId === e.userId ? { ...p, cashedOut: true, cashMultiplier: e.multiplier, payout: e.payout } : p)));
      if (e.userId !== currentUserId()) sound.coin();
    };
    const onMyCashed = (e: any) => {
      setBalance(e.newBalance);
      setMyBet((b) => (b ? { ...b, cashedOut: true, cashMultiplier: e.multiplier, payout: e.payout } : b));
      sound.bigWin();
      telegram.notify('success');
      showToast(`${e.auto ? 'Авто' : ''} ×${e.multiplier.toFixed(2)} · +${formatTon(e.payout)}`);
    };
    const onSettled = (e: any) =>
      setLastWin({ betId: e.betId, multiplier: e.cashMultiplier, payout: e.payout });
    const onCrash = (e: any) => {
      sim.current.phase = 'crashed';
      sim.current.crashPoint = e.crashPoint;
      sim.current.mult = e.crashPoint;
      sim.current.shake = 16;
      sim.current.explodedAt = performance.now();
      spawnExplosion();
      setPhase('crashed');
      if (e.recent) setRecent(e.recent);
      sound.cascade(6);
      telegram.haptic('heavy');
    };

    s.on('round:state', onState);
    s.on('round:betting', onBetting);
    s.on('round:bet', onBet);
    s.on('round:start', onStart);
    s.on('round:tick', onTick);
    s.on('round:cashout', onCashout);
    s.on('crash:cashed', onMyCashed);
    s.on('crash:settled', onSettled);
    s.on('round:crash', onCrash);
    s.emit('crash:sync', null, (snap: any) => snap && applyState(snap));

    return () => {
      s.off('round:state', onState);
      s.off('round:betting', onBetting);
      s.off('round:bet', onBet);
      s.off('round:start', onStart);
      s.off('round:tick', onTick);
      s.off('round:cashout', onCashout);
      s.off('crash:cashed', onMyCashed);
      s.off('crash:settled', onSettled);
      s.off('round:crash', onCrash);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Canvas-цикл ──
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    let raf = 0;

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      if (sim.current.stars.length === 0) {
        sim.current.stars = Array.from({ length: 60 }, () => ({
          x: Math.random() * w,
          y: Math.random() * h,
          r: Math.random() * 1.4 + 0.3,
          s: Math.random() * 0.5 + 0.2,
        }));
      }
    };
    resize();
    window.addEventListener('resize', resize);

    const draw = () => {
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      const S = sim.current;

      // обновляем множитель (локальная интерполяция = формула сервера)
      if (S.phase === 'running' && S.startedAt) {
        const el = (serverNow() - S.startedAt) / 1000;
        S.mult = Math.max(1, Math.exp(CRASH_GROWTH_K * el));
      }
      const m = S.mult;

      // тряска при краше
      let shx = 0, shy = 0;
      if (S.shake > 0) {
        shx = (Math.random() - 0.5) * S.shake;
        shy = (Math.random() - 0.5) * S.shake;
        S.shake *= 0.88;
        if (S.shake < 0.4) S.shake = 0;
      }
      ctx.save();
      ctx.translate(shx, shy);

      // фон
      const grad = ctx.createLinearGradient(0, 0, 0, h);
      grad.addColorStop(0, '#0a1626');
      grad.addColorStop(1, '#0b0f1a');
      ctx.fillStyle = grad;
      ctx.fillRect(-20, -20, w + 40, h + 40);

      // звёзды (лёгкий дрейф вниз при росте)
      const drift = S.phase === 'running' ? (m - 1) * 6 : 0;
      ctx.fillStyle = 'rgba(255,255,255,0.6)';
      for (const st of S.stars) {
        const yy = (st.y + drift * st.s) % h;
        ctx.globalAlpha = 0.3 + st.s;
        ctx.beginPath();
        ctx.arc(st.x, yy, st.r, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;

      const padL = 14, padB = 26, padT = 14;
      const plotW = w - padL - 14;
      const plotH = h - padB - padT;

      const crashed = S.phase === 'crashed' || S.phase === 'cooldown';
      const running = S.phase === 'running';

      if (running || crashed) {
        // авто-зум: держим голову кривой в кадре
        const tNow = Math.max(0.0001, Math.log(m) / CRASH_GROWTH_K);
        const maxT = Math.max(tNow, 2.2);
        const maxY = Math.max(m - 1, 0.6);
        const X = (t: number) => padL + (t / maxT) * plotW;
        const Y = (mm: number) => padT + plotH - ((mm - 1) / maxY) * plotH;

        // кривая
        const steps = 64;
        const col = crashed ? '#ff4d5e' : m >= 10 ? '#ffd24a' : m >= 3 ? '#18d6ff' : '#00e701';
        ctx.beginPath();
        for (let i = 0; i <= steps; i++) {
          const t = (tNow * i) / steps;
          const mm = Math.exp(CRASH_GROWTH_K * t);
          const px = X(t), py = Y(mm);
          if (i === 0) ctx.moveTo(px, py);
          else ctx.lineTo(px, py);
        }
        // заливка под кривой
        ctx.lineTo(X(tNow), Y(1));
        ctx.lineTo(X(0), Y(1));
        ctx.closePath();
        const fill = ctx.createLinearGradient(0, padT, 0, h);
        fill.addColorStop(0, crashed ? 'rgba(255,77,94,0.28)' : 'rgba(0,231,1,0.28)');
        fill.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = fill;
        ctx.fill();

        // линия кривой
        ctx.beginPath();
        for (let i = 0; i <= steps; i++) {
          const t = (tNow * i) / steps;
          const mm = Math.exp(CRASH_GROWTH_K * t);
          const px = X(t), py = Y(mm);
          if (i === 0) ctx.moveTo(px, py);
          else ctx.lineTo(px, py);
        }
        ctx.strokeStyle = col;
        ctx.lineWidth = 3;
        ctx.shadowColor = col;
        ctx.shadowBlur = 14;
        ctx.stroke();
        ctx.shadowBlur = 0;

        // ракета на голове
        const hx = X(tNow), hy = Y(m);
        if (!crashed) {
          const ang = Math.atan2(-(CRASH_GROWTH_K * Math.exp(CRASH_GROWTH_K * tNow)) * (plotH / maxY), plotW / maxT);
          drawRocket(ctx, hx, hy, ang, m);
        }
      }

      // частицы взрыва
      if (S.particles.length) {
        for (const p of S.particles) {
          p.x += p.vx;
          p.y += p.vy;
          p.vy += 0.12;
          p.life -= 0.02;
          if (p.life > 0) {
            ctx.globalAlpha = Math.max(0, p.life);
            ctx.fillStyle = p.c;
            ctx.beginPath();
            ctx.arc(p.x, p.y, 2.6, 0, Math.PI * 2);
            ctx.fill();
          }
        }
        ctx.globalAlpha = 1;
        S.particles = S.particles.filter((p) => p.life > 0);
      }

      // текст множителя / статуса
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      if (S.phase === 'betting') {
        const left = Math.max(0, (S.betEndsAt - serverNow()) / 1000);
        ctx.fillStyle = '#8aa0b6';
        ctx.font = '600 14px Inter, sans-serif';
        ctx.fillText('Делайте ставки', w / 2, h / 2 - 16);
        ctx.fillStyle = '#fff';
        ctx.font = '800 40px Inter, sans-serif';
        ctx.fillText(left.toFixed(1) + 's', w / 2, h / 2 + 18);
      } else if (running || crashed) {
        const big = crashed ? '#ff4d5e' : m >= 10 ? '#ffd24a' : '#ffffff';
        ctx.fillStyle = big;
        ctx.shadowColor = big;
        ctx.shadowBlur = crashed ? 18 : 10;
        ctx.font = '900 52px Inter, sans-serif';
        ctx.fillText('×' + m.toFixed(2), w / 2, h / 2 - 6);
        ctx.shadowBlur = 0;
        if (crashed) {
          ctx.fillStyle = '#ff8088';
          ctx.font = '700 15px Inter, sans-serif';
          ctx.fillText('Разбилась', w / 2, h / 2 + 34);
        }
      } else {
        ctx.fillStyle = '#8aa0b6';
        ctx.font = '600 14px Inter, sans-serif';
        ctx.fillText('Подключение…', w / 2, h / 2);
      }

      ctx.restore();

      // живой payout на кнопке кэшаута
      const mine = myBetRef.current;
      if (cashRef.current && mine && !mine.cashedOut && running) {
        cashRef.current.textContent = `ЗАБРАТЬ ×${m.toFixed(2)} · +${formatTon((parseFloat(mine.amount) * m).toFixed(2))}`;
      }

      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const spawnExplosion = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const w = canvas.clientWidth;
    const S = sim.current;
    // позиция головы примерно справа-сверху
    const cx = w * 0.72, cy = canvas.clientHeight * 0.32;
    const colors = ['#ff4d5e', '#ffd24a', '#ff8a3d', '#ffffff'];
    S.particles = Array.from({ length: 38 }, () => {
      const a = Math.random() * Math.PI * 2;
      const sp = Math.random() * 5 + 1.5;
      return { x: cx, y: cy, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp - 1, life: 1, c: colors[Math.floor(Math.random() * colors.length)] };
    });
  };

  const balanceNum = parseFloat(balance) || 0;
  const amountNum = parseFloat(amount) || 0;
  const canBet = phase === 'betting' && !myBet && amountNum > 0 && amountNum <= balanceNum && !busy;

  const placeBet = () => {
    if (!canBet) {
      if (amountNum > balanceNum) showToast('Недостаточно средств', true);
      return;
    }
    const auto = parseFloat(autoCash);
    const payload: any = { amount: String(amountNum) };
    if (isFinite(auto) && auto >= 1.01) payload.autoCashout = auto;
    setBusy(true);
    telegram.haptic('light');
    getCrashSocket().emit('bet:place', payload, (res: any) => {
      setBusy(false);
      if (res?.error) {
        showToast(res.error, true);
        return;
      }
      setBalance(res.newBalance);
      setMyBet({ amount: String(amountNum), autoCashout: payload.autoCashout ?? null, cashedOut: false, cashMultiplier: null, payout: null });
      sound.coin();
    });
  };

  const cashout = () => {
    telegram.haptic('medium');
    getCrashSocket().emit('bet:cashout', null, (res: any) => {
      if (res?.error) showToast(res.error, true);
    });
  };

  const setAmt = (v: number) => {
    const c = Math.max(0, Math.min(v, balanceNum || v));
    setAmount(c % 1 === 0 ? String(c) : c.toFixed(2));
  };

  const pillColor = (cp: number) => (cp < 2 ? 'lose' : cp < 10 ? 'win' : 'gold');
  const totalBank = players.reduce((a, p) => a + parseFloat(p.amount), 0);

  return (
    <>
      <div className="recent crash-recent">
        {recent.slice(0, 14).map((cp, i) => (
          <span key={i} className={`pill static ${pillColor(cp)}`}>{cp.toFixed(2)}×</span>
        ))}
      </div>

      <div className="card crash-stage">
        <canvas ref={canvasRef} className="crash-canvas" />
      </div>

      {lastWin && (
        <div className="card replay-foot">
          <span className="replay-tag">ТЫ ЗАБРАЛ ×{lastWin.multiplier.toFixed(2)} · +{formatTon(lastWin.payout)}</span>
          <button
            className="btn"
            onClick={() =>
              telegram.share(
                `Зацени реплей: ×${lastWin.multiplier.toFixed(2)} в Ракетке — 1Zov`,
                replayLink(lastWin.betId)
              )
            }
          >
            Поделиться реплеем
          </button>
        </div>
      )}

      <div className="card bet">
        {myBet && phase === 'running' && !myBet.cashedOut ? (
          <button ref={cashRef} className="btn cashout" onClick={cashout}>ЗАБРАТЬ</button>
        ) : myBet && myBet.cashedOut ? (
          <button className="btn ghost" disabled>
            Выведено ×{myBet.cashMultiplier?.toFixed(2)} · +{formatTon(myBet.payout || '0')}
          </button>
        ) : myBet ? (
          <button className="btn ghost" disabled>
            Ставка принята{myBet.autoCashout ? ` · авто ×${myBet.autoCashout}` : ''} — ждём старт
          </button>
        ) : (
          <>
            <div className="label-row">
              <span className="label">Ставка</span>
              <span className="muted">баланс {formatTon(balance)}</span>
            </div>
            <div className="field">
              <span className="gem">◆</span>
              <input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ''))} />
              <div className="mini-actions">
                <button onClick={() => setAmt(amountNum / 2)}>½</button>
                <button onClick={() => setAmt(amountNum * 2)}>2×</button>
                <button onClick={() => setAmt(balanceNum)}>MAX</button>
              </div>
            </div>
            <div className="field">
              <span className="gem" style={{ fontSize: 12 }}>×</span>
              <input inputMode="decimal" value={autoCash} onChange={(e) => setAutoCash(e.target.value.replace(/[^0-9.]/g, ''))} placeholder="авто-кэшаут" />
              <span className="muted" style={{ paddingRight: 10, fontSize: 12 }}>авто</span>
            </div>
            <button className="btn" disabled={!canBet} onClick={placeBet}>
              {phase === 'betting' ? 'Поставить' : 'Ждём след. раунд'}
            </button>
          </>
        )}
      </div>

      <div className="card crash-players">
        <div className="label-row">
          <span className="label">В раунде: {players.length}</span>
          <span className="muted">банк {formatTon(totalBank.toFixed(2))}</span>
        </div>
        <div className="crash-list">
          {players.length === 0 && <div className="muted" style={{ padding: '8px 0' }}>Пока никто не поставил</div>}
          {players
            .slice()
            .sort((a, b) => parseFloat(b.amount) - parseFloat(a.amount))
            .slice(0, 12)
            .map((p) => (
              <div key={p.userId} className="crash-row">
                <span className="crash-row-name">{p.name}</span>
                <span className="crash-row-amt">{formatTon(p.amount)}</span>
                <span className={`crash-row-mult ${p.cashedOut ? 'won' : ''}`}>
                  {p.cashedOut ? `×${p.cashMultiplier?.toFixed(2)}` : '—'}
                </span>
              </div>
            ))}
        </div>
      </div>
    </>
  );
}

function currentUserId(): number {
  try {
    const t = getAuthToken();
    if (!t) return -1;
    return JSON.parse(atob(t.split('.')[1])).sub ?? -1;
  } catch {
    return -1;
  }
}

function drawRocket(ctx: CanvasRenderingContext2D, x: number, y: number, ang: number, m: number) {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(-ang);
  // пламя
  const flame = 8 + Math.random() * 8;
  const fg = ctx.createLinearGradient(-10 - flame, 0, -10, 0);
  fg.addColorStop(0, 'rgba(255,160,40,0)');
  fg.addColorStop(1, m >= 10 ? '#ffd24a' : '#ff8a3d');
  ctx.fillStyle = fg;
  ctx.beginPath();
  ctx.moveTo(-9, -4);
  ctx.lineTo(-9 - flame, 0);
  ctx.lineTo(-9, 4);
  ctx.closePath();
  ctx.fill();
  // корпус
  ctx.fillStyle = '#eef3f8';
  ctx.beginPath();
  ctx.moveTo(12, 0);
  ctx.lineTo(-6, -5);
  ctx.lineTo(-6, 5);
  ctx.closePath();
  ctx.fill();
  // нос
  ctx.fillStyle = '#ff4d5e';
  ctx.beginPath();
  ctx.moveTo(12, 0);
  ctx.lineTo(4, -3.2);
  ctx.lineTo(4, 3.2);
  ctx.closePath();
  ctx.fill();
  // иллюминатор
  ctx.fillStyle = '#18d6ff';
  ctx.beginPath();
  ctx.arc(1, 0, 1.8, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}
